import React, { useMemo } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";
import { NewsDayCount } from "../../../models/newsModels";
import { EvidenceDirection, NewsBarUnit } from "../../../enums/News";
import {
  compassLightGreen,
  lightGreyOutline,
  lightGreyText,
} from "../../../styles";
import {
  DIRECTION_COLORS,
  DIRECTION_LABELS,
  LEGEND_DIRECTIONS,
  STACK_ORDER,
} from "./constants";
import {
  NewsBarBucket,
  bucketNewsDays,
  dayTotal,
  formatNewsBucket,
  formatNewsSpan,
  isDayOnOrAfter,
} from "./utils";

type BarWidth = number | "fill";

type NewsDayBarsProps = {
  days: NewsDayCount[];
  height: number;
  barWidth: BarWidth;
  newSince?: string | null;
  showAxis?: boolean;
};

const Bars = styled.div<{ $height: number }>`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  max-width: 100%;
  overflow: hidden;
  height: ${({ $height }): number => $height}px;
`;

const widthRule = ({ $width }: { $width: BarWidth }): string =>
  $width === "fill"
    ? "flex: 1 1 0; min-width: 2px;"
    : `width: ${$width}px; min-width: ${$width}px;`;

const Column = styled.div<{ $width: BarWidth; $height: number }>`
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  ${widthRule}
  height: ${({ $height }): number => $height}px;
`;

const Segment = styled.div<{ $color: string; $share: number }>`
  height: ${({ $share }): number => $share * 100}%;
  min-height: 3px;
  flex-shrink: 0;
  background-color: ${({ $color }): string => $color};
  &:last-child {
    border-radius: 2px 2px 0 0;
  }
`;

const Baseline = styled.div<{ $width: BarWidth }>`
  ${widthRule}
  height: 1px;
  background-color: ${lightGreyOutline};
`;

const MIN_SHARE = 0.12;

const AxisLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 10.5px;
  color: ${lightGreyText};
`;

const axisLabels = (buckets: NewsBarBucket[]): [string, string] => {
  const first = buckets[0];
  const last = buckets[buckets.length - 1];
  if (first.unit === NewsBarUnit.MONTH) {
    return [formatNewsBucket(first), formatNewsBucket(last)];
  }
  return formatNewsSpan(first.start, last.end);
};

const tooltipFor = (bucket: NewsBarBucket): string => {
  const parts = LEGEND_DIRECTIONS.filter(
    (direction) => bucket.counts[direction] > 0,
  ).map(
    (direction) => `${bucket.counts[direction]} ${DIRECTION_LABELS[direction]}`,
  );
  const other =
    bucket.counts[EvidenceDirection.NEUTRAL] +
    bucket.counts[EvidenceDirection.UNCLEAR];
  const detail = [...parts, ...(other > 0 ? [`${other} other`] : [])].join(
    " · ",
  );
  return `${formatNewsBucket(bucket)} — ${detail}`;
};

const BucketColumn = ({
  bucket,
  peak,
  height,
  barWidth,
  newSince,
}: {
  bucket: NewsBarBucket;
  peak: number;
  height: number;
  barWidth: BarWidth;
  newSince?: string | null;
}): JSX.Element => {
  const total = dayTotal(bucket);
  if (total === 0) return <Baseline $width={barWidth} />;
  const columnHeight = Math.max(total / peak, MIN_SHARE) * height;

  if (newSince !== undefined) {
    const isNew = newSince !== null && isDayOnOrAfter(bucket.end, newSince);
    return (
      <Column $width={barWidth} $height={columnHeight}>
        <Segment
          $color={isNew ? compassLightGreen : lightGreyOutline}
          $share={1}
        />
      </Column>
    );
  }

  return (
    <Tooltip title={tooltipFor(bucket)} mouseEnterDelay={0.1}>
      <Column $width={barWidth} $height={columnHeight}>
        {STACK_ORDER.filter((direction) => bucket.counts[direction] > 0).map(
          (direction) => (
            <Segment
              key={direction}
              $color={DIRECTION_COLORS[direction]}
              $share={bucket.counts[direction] / total}
            />
          ),
        )}
      </Column>
    </Tooltip>
  );
};

export const NewsDayBars = ({
  days,
  height,
  barWidth,
  newSince,
  showAxis = false,
}: NewsDayBarsProps): JSX.Element => {
  const buckets = useMemo(() => bucketNewsDays(days), [days]);
  const peak = Math.max(1, ...buckets.map(dayTotal));
  return (
    <div>
      <Bars $height={height} aria-hidden={newSince !== undefined}>
        {buckets.map((bucket) => (
          <BucketColumn
            key={bucket.start}
            bucket={bucket}
            peak={peak}
            height={height}
            barWidth={barWidth}
            newSince={newSince}
          />
        ))}
      </Bars>
      {showAxis && buckets.length > 0 && (
        <AxisLabels>
          {axisLabels(buckets).map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </AxisLabels>
      )}
    </div>
  );
};
